import { getBranchId } from "../utils/branch";
import React, { useState, useEffect } from "react";
import { Button, Card, Col, Modal, Row, Select, Table, Tooltip, message } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import { Input } from "antd";
import { SearchOutlined, CarOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { getAvailableCabsRadiusRequest } from "../redux/actions/newAction";
import { useAppDispatch, useAppSelector } from "../utils/hooks";


import ace from "../assets/ace2.png";
import bolero from "../assets/bolero2.png";
import eicher from "../assets/Eicher2.png";
import tata407 from "../assets/tata4072.png";
import three from "../assets/three.png";
import { reassignTripRequest, reassignTripReset } from "../redux/actions/liveAction";

const vehicleImageMap: Record<string, string> = {
  tataace: ace,
  bolero: bolero,
  eicher: eicher,
  "tata 407": tata407,
  "three wheeler": three,
};

const { Search } = Input;

const { Option } = Select;

interface AssignFeatureProps {
  reassignOpen: boolean;
  setReassignOpen: (open: boolean) => void;
  selectedRowKey: string | null;
  selectedBookingData?: any;
  onAssignConfirm?: (rowKey: string) => void;
}

interface AssignVehicleType {
  key: string;
  cabid: string;
  distance: string;
  vehicle: string;
  driver: string;
  mobile: string;
  location: string;
  reason: string;
  trips: number;
  amount: string;
  minutes: number;
  length?: string;
  height?: string;
  width?: string;
  startlocation?: string;
}

const StyledTable = styled(Table as React.FC<TableProps<any>>)`
  .ant-table-thead > tr > th {
    background: #884299 !important;
    color: white !important;
    font-weight: 600;
    text-align: center;
    height: 60px;
  }

  .ant-table-thead > tr > th::before {
    display: none !important;
  }

  /* .ant-table-tbody > tr:hover > td {
    background: #faf5ff;
  } */

  .ant-table-tbody > tr {
    height: 60px;
  }
`;

type AssignTableProps = TableProps<AssignVehicleType>;

const AssignVehicleTable: React.FC<AssignTableProps> = (props) => {
  return <StyledTable {...props} />;
};

const ReassignModel: React.FC<AssignFeatureProps> = ({
  reassignOpen,
  setReassignOpen,
  selectedRowKey,
  selectedBookingData,
  onAssignConfirm,
}) => {
  const dispatch = useAppDispatch();
  const {
    availableCabsRadius,
    availableCabsRadiusLoading,
    availableCabsRadiusError,
    selectedCab
  } = useAppSelector((state) => state.newBooking);
  const { reassignTripMessage, reassignTripError, reassignTripLoading } = useAppSelector((state) => state.live);

  //const [open, setOpen] = useState(false);
  const [selectedVehicleRowKey, setSelectedVehicleRowKey] = useState<string | null>(null);

  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!reassignOpen) return;
    const interval = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(interval);
  }, [reassignOpen]);

  useEffect(() => {
    if (reassignTripMessage) {
      message.success(reassignTripMessage || "Trip reassigned successfully");
      setReassignOpen(false);
      setSelectedVehicleRowKey(null);
      setReasons({});
      dispatch(reassignTripReset());
    } else if (reassignTripError) {
      message.error(reassignTripError);
      dispatch(reassignTripReset());
    }
  }, [reassignTripMessage, reassignTripError, dispatch]);

  useEffect(() => {
    if (selectedCab?.cabid) {
      setSelectedVehicleRowKey(selectedCab.cabid);
    }
  }, [selectedCab]);

  const getMinutesSinceCompletion = (lastcompletedtime?: string): number => {
    if (!lastcompletedtime) return 0;
    const completed = new Date(lastcompletedtime).getTime();
    const now = Date.now();
    if (isNaN(completed)) return 0;
    const diffMs = now - completed;
    return diffMs > 0 ? Math.floor(diffMs / 60000) : 0;
  };

  const dataWithKeys = (selectedCab ? [selectedCab] : availableCabsRadius || []).map(
    (cab: any, index: number) => ({
      ...cab,
      key: cab.cabid || String(index),
      minutes: getMinutesSinceCompletion(cab.lastcompletedtime),
    })
  );

  const selectedVehicle = dataWithKeys.find((item: { key: string | null; }) => item.key === selectedVehicleRowKey);

  const handleAssignClick = () => {
    if (!selectedRowKey) {
      message.warning("Please select a row first");
      return;
    }
    dispatch({ type: "GET_AVAILABLE_CABS_RADIUS_RESET" });
    dispatch(
      getAvailableCabsRadiusRequest(
        // Search around the branch of THIS booking, matching Assignmodel.
        // Previously hardcoded, so reassigning a non-Coimbatore trip looked
        // for cabs in Coimbatore.
        selectedBookingData?.cityid ||
        selectedBookingData?.branchid ||
        getBranchId(),
        Number(selectedBookingData?.vehicletypeid),
        selectedBookingData?.fromloclat,
        selectedBookingData?.fromloclong
      )
    );
    setReassignOpen(true);
  };

  const handleAssignConfirm = () => {
    if (reassignTripLoading) return;
    if (!selectedVehicle) {
      message.warning("Please select a vehicle");
      return;
    }

    const selectedReason = selectedVehicleRowKey ? reasons[selectedVehicleRowKey] : null;

    if (!selectedReason) {
      message.warning("Please select a reason");
      return;
    }

    dispatch(
      reassignTripRequest({
        cabid: selectedVehicle.cabid,
        oldcabid: selectedBookingData.cabid,
        bookid: selectedBookingData.id,
        driverid: selectedVehicle.driverid,
        reassignby: localStorage.getItem("username") || "",
        emptyKM: Number(selectedVehicle.distance),
        reason: selectedReason,
      })
    );
  };

  const handleReasonChange = (value: string, key: string) => {
    setReasons((prev) => ({ ...prev, [key]: value }));
  };

  const isWithinHomeWindow = (): boolean => {
    const now = new Date();
    const hours = now.getHours();
    return hours >= 18 && hours < 24;
  };

  const getAssignColumns = (): ColumnsType<AssignVehicleType> => {
    const columns: ColumnsType<AssignVehicleType> = [
      {
        title: "Cab ID",
        dataIndex: "cabid",
        width: 100,
        align: "center",
      },
      {
        title: "Distance",
        dataIndex: "distance",
        align: "center",
        width: 100,
      },
      {
        title: "Vehicle",
        dataIndex: "cabtype",
        align: "center",
        width: 100,
      },
      {
        title: "Driver Name",
        dataIndex: "drivername",
        align: "center",
        width: 150,
      },
      {
        title: "Mobile No",
        dataIndex: "drivermobileno",
        width: 120,
        align: "center",
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "fromloc",
        align: "center",
        width: 150,
        ellipsis: true,
        render: (text: string) => (
          <Tooltip title={text} placement="topLeft">
            <span>{text || "-"}</span>
          </Tooltip>
        ),
      },
    ];

    if (isWithinHomeWindow()) {
      columns.push({
        title: "Home",
        dataIndex: "startlocation",
        align: "center",
        width: 150,
        ellipsis: true,
        render: (text: string) => (
          <Tooltip title={text} placement="topLeft">
            <span>{text || "-"}</span>
          </Tooltip>
        ),
      });
    }

    columns.push(
      {
        title: "Reason",
        dataIndex: "reason",
        width: 240,
        align: "center",
        render: (_, record) => (
          <Select
            value={reasons[record.key] || undefined}
            style={{ width: "auto" }}
            placeholder="Select Reason"
            onChange={(value) => handleReasonChange(value, record.key)}
            showSearch
            optionFilterProp="children"
          >
            <Select.Option value="Vehicle Issue">Vehicle Issue</Select.Option>
            <Select.Option value="Trip Not Taken">Trip Not Taken</Select.Option>
            <Select.Option value="Location Change - Tech">
              Location Change - Tech
            </Select.Option>
            <Select.Option value="Location Change - Driver">
              Location Change - Driver
            </Select.Option>
            <Select.Option value="Long Distance">Long Distance</Select.Option>
            <Select.Option value="Location Change - Customer">
              Location Change - Customer
            </Select.Option>
            <Select.Option value="Wrongly Assigned">
              Wrongly Assigned
            </Select.Option>
            <Select.Option value="Wrongly Booked">Wrongly Booked</Select.Option>
            <Select.Option value="Driver Have Another Trip">
              Driver Have Another Trip
            </Select.Option>
            <Select.Option value="Driver Logout">Driver Logout</Select.Option>
            <Select.Option value="Vehicle Change">Vehicle Change</Select.Option>
            <Select.Option value="Document Complaint">
              Document Complaint
            </Select.Option>
            <Select.Option value="Traffic">Traffic</Select.Option>
            <Select.Option value="Driver No Response">
              Driver No Response
            </Select.Option>
            <Select.Option value="Tarpaulin Not Available">
              Tarpaulin Not Available
            </Select.Option>
            <Select.Option value="Other">Other</Select.Option>
          </Select>
        ),
      },
      {
        title: "Total Trips",
        dataIndex: "totaltrip",
        align: "center",
        width: 100,
      },
      {
        title: "Total Amount",
        dataIndex: "tripfare",
        align: "center",
        width: 120,
      },
      {
        title: "Minutes",
        dataIndex: "minutes",
        align: "center",
        width: 100,
      }
    );

    return columns;
  };

  return (
    <>
      <Button className="action-btn2" onClick={handleAssignClick} disabled={!selectedRowKey}>
        Reassign
      </Button>

      <Modal
        title="Reassign Vehicle"
        open={reassignOpen}
        centered
        onOk={handleAssignConfirm}
        confirmLoading={reassignTripLoading}
        closable={false}
        width={{ xs: "96vw", lg: "95vw", xxl: 1500 }}
        footer={
          <Row justify="center" style={{ gap: "16px" }}>
            <Button
              type="primary"
              size="large"
              onClick={handleAssignConfirm}
              loading={reassignTripLoading}
              style={{ minWidth: 120 }}
            >
              Reassign Vehicle
            </Button>
            <Button type="primary" size="large" style={{ minWidth: 120 }} onClick={() => {
              setReassignOpen(false);
              setSelectedVehicleRowKey(null);
            }}>Cancel</Button>
          </Row>
        }
        styles={{
          header: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            background: "#884299",
            color: "#ffffff",
            borderBottom: "none",
            padding: "16px 24px",
          },
          title: {
            color: "#ffffff",
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: 0.5,
          },
          body: {
            paddingTop: 20,
          },
        }}
      >
        <Card
          bordered={false}
          style={{ background: "#cbb5e2", borderRadius: 12 }}
        >
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <strong style={{ fontSize: 18 }}>Booking :</strong>
              <div>{selectedBookingData?.id || selectedBookingData?.BookId || "-"}</div>
            </Col>

            <Col span={6}>
              <strong style={{ fontSize: 18 }}>From :</strong>
              <div>{selectedBookingData?.fromloc || "-"}</div>
            </Col>

            <Col span={6}>
              <strong style={{ fontSize: 18 }}>To :</strong>
              <div>{selectedBookingData?.toloc || "-"}</div>
            </Col>

            <Col span={6}>
              <strong style={{ fontSize: 18 }}>Remarks :</strong>
              <div>{selectedBookingData?.remarks || "-"}</div>
            </Col>
          </Row>
        </Card>

        <Card
          style={{
            marginTop: 16,
            borderRadius: 12,
            backgroundColor: "#bde0fe34",
          }}
        >
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 12 }}
          >
            <Col>
              <h3 style={{ margin: 0, fontSize: 18 }}>Available Vehicles</h3>
            </Col>

            <Col>
              <Search
                placeholder="Search by Cab ID / Driver / Vehicle"
                allowClear
                enterButton={<SearchOutlined />}
                style={{ width: "100%", maxWidth: 300, minWidth: 180 }}
              />
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={19}>
              <AssignVehicleTable
                key={tick}
                loading={availableCabsRadiusLoading}
                rowSelection={{
                  type: "radio",
                  selectedRowKeys: selectedVehicleRowKey ? [selectedVehicleRowKey] : [],
                  onChange: (keys) => setSelectedVehicleRowKey(keys[0] as string),
                }}
                columns={getAssignColumns()}
                dataSource={dataWithKeys}
                pagination={{ pageSize: 5 }}
                size="small"
                bordered
                scroll={{ x: 1450, y: 300 }}
              />
            </Col>

            <Col xs={24} lg={5}>
              <div
                style={{
                  height: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  background: "#fff",
                  borderRadius: 12,
                  padding: 16,
                  border: "1px solid #d9d9d9",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                }}
              >
                <div style={{
                  width: "100%",
                  textAlign: "center",
                  marginBottom: 16,
                  paddingBottom: 8,
                  borderBottom: "1px solid #f0f0f0"
                }}>
                  <strong style={{ fontSize: 16, color: "#ffffffff", display: "block", backgroundColor: "#884299", padding: 10, borderRadius: 12 }}>
                    {selectedVehicle ? selectedVehicle.cabtype : "Select a Vehicle"}
                  </strong>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                    height: "100%",
                    width: "100%",
                    minHeight: 200,
                    background: "#f9f9f9",
                    borderRadius: 8,
                    overflow: "auto"
                  }}
                >
                  {/* <div>
                    {selectedVehicle && (
                      <div style={{ display: "flex", flexDirection: "column", fontSize: 16, color: "#884299", marginTop: 4, fontWeight: "bold" }}>
                        {selectedVehicle.length && (
                          <div>Length: <span style={{ color: "#000" }}>{selectedVehicle.length}</span></div>
                        )}

                        {selectedVehicle.width && (
                          <div>Width: <span style={{ color: "#000" }}>{selectedVehicle.width}</span></div>
                        )}

                        {selectedVehicle.height && (
                          <div>Height: <span style={{ color: "#000" }}>{selectedVehicle.height}</span></div>
                        )}
                      </div>
                    )}
                  </div> */}
                  <div>
                    {selectedVehicle ? (
                      <img
                        src={vehicleImageMap[selectedVehicle.cabtype.toLowerCase()] || ace}
                        alt={selectedVehicle.cabtype}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "180px",
                          objectFit: "contain",
                          filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.1))"
                        }}
                      />
                    ) : (
                      <div style={{ textAlign: "center", color: "#bfbfbf" }}>
                        <CarOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
                        <div>Please select a vehicle from the table to see details</div>
                      </div>
                    )}
                  </div>
                </div>

                {selectedVehicle && (
                  <div style={{ marginTop: 16, width: "100%", fontSize: 13 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ color: "#8c8c8c" }}>Cab ID:</span>
                      <span style={{ fontWeight: 600 }}>{selectedVehicle.cabid}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ color: "#8c8c8c" }}>Driver:</span>
                      <span style={{ fontWeight: 600 }}>{selectedVehicle.drivername}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#8c8c8c" }}>Distance:</span>
                      <span style={{ fontWeight: 600, color: "#52c41a" }}>{selectedVehicle.distance}</span>
                    </div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Card>
      </Modal>
    </>
  );
};

export default ReassignModel;
